import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Anchor, Button, Select, Skeleton } from '@any-ui-react/core';
import classNames from 'classnames';
import { z } from 'zod';

import { ConditionCombinator, DefaultOperator, OperatorSelector } from '../../condition-builder';

export const RichMenuPostbackRuleCardErrors = z
  .object({
    richMenuIdError: z.string().optional(),
    triggerError: z.string().optional(),
  })
  .optional();

export type RichMenuPostbackRuleCardErrors = z.infer<typeof RichMenuPostbackRuleCardErrors>;

interface RichMenuPostbackRuleCardProps {
  onClick: () => void;
  postbackOptions?: { label: string; value: string }[];
  richMenuId?: string;
  trigger?: string;
  operator?: string;
  onSelect?: (value: string | null) => void;
  onOperatorChange: (value?: string) => void;
  to: string;
  title?: string;
  loading?: boolean;
  errors?: RichMenuPostbackRuleCardErrors;
}

export const RichMenuPostbackRuleCard = ({
  onClick,
  richMenuId,
  trigger,
  postbackOptions,
  onOperatorChange,
  onSelect,
  to,
  title,
  operator,
  loading,
  errors,
}: RichMenuPostbackRuleCardProps) => {
  const { t } = useTranslation('crm');

  if (!richMenuId) {
    return (
      <div
        className={classNames('flex items-center gap-x-2 rounded-sm border p-2', {
          'border-failure': !!errors,
        })}
      >
        <ConditionCombinator className="shrink-0" type="IF" />
        <div className="relative">
          <Button type="button" variant="default" onClick={onClick}>
            {t('crm.richMenuSelector.postback.button.selectRichMenu')}
          </Button>
          {errors?.richMenuIdError && (
            <p className="text-3xs text-failure absolute">{errors.richMenuIdError}</p>
          )}
        </div>
      </div>
    );
  }

  if (loading) {
    return <Skeleton />;
  }

  return (
    <div
      className={classNames(
        'crm-builder__rule-card grid grid-cols-[min-content_1fr] items-center gap-x-2 rounded-sm border p-2',
        {
          'border-failure': !!errors,
        }
      )}
    >
      <ConditionCombinator className="shrink-0" type="IF" />
      <div>
        <Trans
          i18nKey="crm.richMenuSelector.postback.selectorRow.title"
          ns="crm"
          components={[<Anchor component="button" type="button" onClick={onClick} />]}
          parent="p"
          values={{ title }}
        />
      </div>
      <div className="relative col-start-2 flex h-12 items-center gap-x-2">
        {postbackOptions && postbackOptions?.length > 0 ? (
          <>
            <p>{t('crm.richMenuSelector.postback.selectorRow.postbackKey')}</p>
            <OperatorSelector
              operators={[DefaultOperator.enum.EQUALS]}
              value={operator}
              onSelect={onOperatorChange}
            />
            <Select
              error={errors?.triggerError}
              classNames={{
                error: 'absolute',
                input: 'mb-0',
              }}
              data={postbackOptions ?? []}
              value={trigger ?? ''}
              placeholder={t('crm.richMenuSelector.postback.selectorRow.selector.placeholder')}
              onChange={onSelect}
            />
          </>
        ) : (
          <Trans
            i18nKey="crm.richMenuSelector.postback.selectorRow.noOptions"
            ns="crm"
            components={[<Anchor component={Link} to={to} />]}
            parent="p"
            values={{ title }}
          />
        )}
      </div>
    </div>
  );
};
